// @ts-check
import clsx from 'clsx'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { AuthContext } from '../../providers/AuthContextProvider'
import { NotificationContext } from '../../providers/NotificationContextProvider'
import AlertBanner from './AlertBanner'
import AvatarButton from './AvatarButton'
import CategoryNav from './CategoryNav'
import useActiveNavLink from './hooks/useActiveNavLink'
import useFocus from './hooks/useFocus'
import Button from '../settings/common/Button'
import NavMenuPopover from './NavMenuPopover'
import NavMenuToggleButton from './NavMenuToggleButton'
import NewsNavMenu from './NewsNavMenu'
import PeopleNavMenu from './PeopleNavMenu'
import SiteSearch from './SiteSearch'
import styles from './TopNav.module.css'
import UserMenuPopover, { toInitials } from './UserMenuPopover'
import Logo from '../Logo'
import Overlay from '../Overlay/Overlay'
import AuthenticationForm from '../Registration'
import Modal from '../Registration/Modal'

/** @typedef {[string, boolean]} LoginModalRule */
/** @type {LoginModalRule[]} */
const LOGIN_MODAL_RULES = [
  ['mdx', true],
  ['oftheminute', true],
  ['newfaces', true],
  ['work', false],
  ['db', false],
  ['rankings', false],
]

/**
 * @param {string} pathname
 * @param {URLSearchParams | null} searchParams
 * @returns {boolean}
 */
const isLoginModalPath = (pathname = '', searchParams = null) => {
  if (isInstagramTraffic(searchParams)) {
    return false
  }

  const pathComponents = getPathComponents(pathname)

  return LOGIN_MODAL_RULES.some(([base, includeBase]) => {
    return pathComponents[0] === base && (pathComponents.length > 1 || includeBase)
  })
}

/**
 * @param {string} pathname
 * @returns {string[]}
 */
const getPathComponents = (pathname) => {
  return pathname.split('/').filter((item) => item !== '')
}

// Detect Instagram in-app browser via user agent
const isInstagramBrowser = () => {
  return /instagram/i.test(navigator.userAgent)
}

// Detect Instagram query parameter (source=instagram, case-insensitive)
/**
 * @param {URLSearchParams | null} searchParams
 * @returns {boolean}
 */
const hasInstagramSourceParam = (searchParams) => {
  if (!searchParams) return false
  const source = searchParams.get('source')
  return source?.toLowerCase() === 'instagram'
}

// Combined check for any Instagram traffic
/**
 * @param {URLSearchParams | null} searchParams
 * @returns {boolean}
 */
const isInstagramTraffic = (searchParams) => {
  return isInstagramBrowser() || hasInstagramSourceParam(searchParams)
}

/**
 * Helper function to get the pre-auth subtitle based on current page
 * @param {string} pathname
 * @returns {string}
 */
const getPreAuthSubtitle = (pathname) => {
  const title = document.title
  const pathComponents = getPathComponents(pathname)
  const basePath = pathComponents[0]

  if (basePath === 'rankings') {
    return `${title} Rankings`
  }

  if (basePath === 'mdx') {
    if (pathComponents.length > 1) {
      return title
    }
    return 'Cover stories'
  }

  if (basePath === 'oftheminute') {
    if (pathComponents.length > 1) {
      return title
    }
    return 'News & Interviews'
  }

  if (basePath === 'newfaces') {
    if (pathComponents.length > 1) {
      return title
    }
    return "Models.com's NewFaces"
  }

  if (basePath === 'work') {
    return title
  }

  if (basePath === 'search') {
    return 'the Models.com ProSearch'
  }

  if (basePath === 'db') {
    return 'Models.com fashion database Pro updates'
  }

  return title
}

export default function TopNav() {
  const { loading, userSettings } = useContext(AuthContext)
  const { hasNotifications } = useContext(NotificationContext)
  const { activeLink } = useActiveNavLink()
  const [userMenuPopoverOpen, setUserMenuPopoverOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [siteSearchActive, setSiteSearchActive] = useState(false)
  const [topNavHeight, setTopNavHeight] = useState(0)
  const [showLoginModule, setShowLoginModule] = useState(false)
  const [showPreAuth, setShowPreAuth] = useState(false)
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const [activeAuthView, setActiveAuthView] = useState(
    /** @type {string | undefined} */ (undefined)
  )
  /** @type {{ authenticated?: boolean } | any} */
  const settings = userSettings
  const isLoginModalFlow =
    !settings?.authenticated && isLoginModalPath(location.pathname, searchParams)

  useEffect(() => {
    /**
     * @param {Element} element
     * @param {string} type
     */
    function attachClickHandler(element, type) {
      element.addEventListener('click', (event) => {
        event.preventDefault()
        document.querySelectorAll('.close-reveal-modal').forEach((ele) => {
          if (ele instanceof HTMLElement) {
            ele.click()
          }
        })
        setActiveAuthView(type)
        setShowLoginModule(true)
      })
    }

    // Get all existing login launcher elements
    const existingLoginLauncherElements = document.querySelectorAll('.login-flow-launcher')
    existingLoginLauncherElements.forEach((element) => attachClickHandler(element, 'login'))

    // Get all existing register launcher elements
    const existingRegisterLauncherElements = document.querySelectorAll('.register-flow-launcher')
    existingRegisterLauncherElements.forEach((element) => attachClickHandler(element, 'register'))

    // Create a MutationObserver to watch for new elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element) {
              node
                .querySelectorAll('.login-flow-launcher')
                .forEach((/** @type {Element} */ launcher) => attachClickHandler(launcher, 'login'))
              node
                .querySelectorAll('.register-flow-launcher')
                .forEach((/** @type {Element} */ launcher) =>
                  attachClickHandler(launcher, 'register')
                )
            }
          })
        }
      })
    })

    // Configure the observer to watch for child list mutations
    const config = {
      childList: true,
      subtree: true,
    }

    // Start observing the document body
    observer.observe(document.body, config)

    return () => {
      observer.disconnect()
    }
  }, [])

  const topNavRef = useCallback(
    /**
     * @param {HTMLDivElement | null} node
     */
    (node) => {
      if (node) {
        const height = node.getBoundingClientRect().height
        setTopNavHeight(height)
        const win = /** @type {any} */ (window)
        win.topNavOffset = height
      }
    },
    [hasNotifications, loading]
  )
  const handleUserNavToggle = () => {
    setUserMenuPopoverOpen((prev) => !prev)
    if (menuOpen) {
      setMenuOpen(false)
    }
  }
  /**
   * @param {boolean} value
   */
  const handleNavMenuToggle = (value) => {
    setMenuOpen(value)
    if (userMenuPopoverOpen) {
      setUserMenuPopoverOpen(false)
    }
  }
  const handleShowLoginModule = () => {
    // Only show the pre-auth modal for login modal flow; other flows go straight to Login overlay
    if (isLoginModalFlow) {
      setShowPreAuth(true)
      setShowLoginModule(false)
    } else {
      setShowLoginModule(true)
    }
  }
  const handleCloseLoginModule = () => {
    setShowLoginModule(false)
  }
  const { ref, setOnBlur } = useFocus()
  useEffect(() => {
    if (setOnBlur) {
      setOnBlur(() => {
        setUserMenuPopoverOpen(false)
      })
    }
  }, [setOnBlur])

  useEffect(() => {
    if (loading) return
    const isResetFlow = searchParams.has('reset')
    const isForgotFlow =
      searchParams.has('forgot') || location.pathname === '/register/resetword.html'
    const isRegisterFlow =
      !settings?.authenticated &&
      (location.pathname === '/register' || location.pathname === '/register/')
    const isSecureMemberpayFlow =
      !settings?.authenticated && location.pathname.startsWith('/securememberpay')
    const isSocialRegisterFlow = searchParams.has('source')
    const isEmailLoginFlow = location.pathname === '/account/email-verification.html'
    let isLoginFlow = false
    if (!isEmailLoginFlow) {
      isLoginFlow = location.pathname.startsWith('/account/') && !settings?.authenticated
    }
    if (isLoginModalFlow || isLoginFlow || isSecureMemberpayFlow) {
      setActiveAuthView('login')
    } else if (isRegisterFlow) {
      setActiveAuthView(isSocialRegisterFlow ? 'register-social' : 'register')
    } else if (isResetFlow) {
      setActiveAuthView('resetPassword')
    } else if (isForgotFlow) {
      setActiveAuthView('forgotPassword')
    }

    // Show pre-auth only for login modal flow, otherwise follow existing logic
    const shouldShowPreAuth =
      isLoginModalFlow &&
      !isLoginFlow &&
      !isSecureMemberpayFlow &&
      !isResetFlow &&
      !isForgotFlow &&
      !isRegisterFlow

    setShowPreAuth(shouldShowPreAuth)

    setShowLoginModule(
      !shouldShowPreAuth &&
        (isLoginModalFlow ||
          isLoginFlow ||
          isResetFlow ||
          isRegisterFlow ||
          isForgotFlow ||
          isSecureMemberpayFlow)
    )
  }, [loading, settings, searchParams, location.pathname, isLoginModalFlow])
  return (
    <>
      <div className={styles.TopNav} ref={topNavRef}>
        <div className={styles.container}>
          <NavMenuToggleButton open={menuOpen} onToggle={handleNavMenuToggle} />
          <NavMenuPopover open={menuOpen} userSettings={settings} />
          <a href="/" className={styles.logo}>
            <Logo className="" />
          </a>
          <nav className={clsx(styles.nav, styles.navPrimary)}>
            <div
              className={clsx(styles.link, {
                [styles.linkActive]: activeLink === 'rankings',
              })}
            >
              <a href="/rankings/">Rankings</a>
            </div>
            <div
              className={clsx(styles.link, {
                [styles.linkActive]: activeLink === 'agencies',
              })}
            >
              <a href="/agencies/">Agencies</a>
            </div>
            <div
              className={clsx(styles.link, {
                [styles.linkActive]: activeLink === 'people',
              })}
            >
              <PeopleNavMenu />
            </div>
          </nav>

          <SiteSearch
            className={styles.hideSmall}
            onBlur={() => setSiteSearchActive(false)}
            onFocus={() => setSiteSearchActive(true)}
          />

          <nav className={clsx(styles.nav)}>
            <div
              className={clsx(styles.link, {
                [styles.linkActive]: activeLink === 'updates',
              })}
            >
              <NewsNavMenu>
                <a href="/db/advertising" className={styles.iconLink}>
                  <i aria-hidden="true" className="fal fa-clone"></i>
                  News
                </a>
              </NewsNavMenu>
            </div>
            <div
              className={clsx(styles.link, {
                [styles.linkActive]: activeLink === 'lists',
              })}
            >
              <a href="/lists/" className={styles.iconLink}>
                <i aria-hidden="true" className="fal fa-heart"></i>
                Lists
              </a>
            </div>
          </nav>
          <div className={clsx(styles.userMenu, { [styles.ghost]: loading })} ref={ref}>
            {settings?.authenticated ? (
              <AvatarButton
                hasNotifications={hasNotifications}
                avatarImageSrc={settings?.userPicture}
                initials={toInitials(settings?.name)}
                open={userMenuPopoverOpen}
                onToggle={handleUserNavToggle}
              >
                <UserMenuPopover open={userMenuPopoverOpen} userSettings={settings} />
              </AvatarButton>
            ) : (
              <Button onClick={handleShowLoginModule} disabled={showPreAuth || showLoginModule}>
                Login
              </Button>
            )}
          </div>
        </div>
        <SiteSearch
          className={styles.showSmall}
          onBlur={() => setSiteSearchActive(false)}
          onFocus={() => setSiteSearchActive(true)}
        />
        <div className={styles.subNavContent}>
          <div className="rainbow"></div>
          <CategoryNav className={styles.hideSmall} />
          <AlertBanner />
        </div>
      </div>
      <div style={{ height: `${topNavHeight}px`, visibility: 'hidden' }} />
      {(showPreAuth || showLoginModule) && (
        <Overlay
          dismissible={showLoginModule}
          onClick={
            showLoginModule
              ? isLoginModalFlow
                ? () => setShowLoginModule(false)
                : handleCloseLoginModule
              : undefined
          }
          // Z-index is 19 when showing pre-auth (below top nav) and 100 when showing login form (above top nav)
          zIndex={showLoginModule ? 100 : 19}
        >
          {showPreAuth && !showLoginModule && (
            <Modal
              title="Login or Register (Free)"
              // Need the margin-bottom override for foundation. Can remove once foundation is gone.
              body={
                <p style={{ marginBottom: '0' }}>
                  Login or register to view {getPreAuthSubtitle(location.pathname)}
                </p>
              }
              primaryLabel="Login / Register"
              isOpen={true}
              onPrimary={() => {
                setActiveAuthView('login')
                setShowLoginModule(true)
              }}
            />
          )}
          {showLoginModule && (
            <AuthenticationForm
              defaultActiveView={activeAuthView}
              onClose={
                isLoginModalFlow
                  ? () => {
                      setShowLoginModule(false)
                      setShowPreAuth(true)
                    }
                  : handleCloseLoginModule
              }
            />
          )}
        </Overlay>
      )}
    </>
  )
}
